import { guardarAutorizacion } from '../guardarAutorizacion';
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Input, message, Typography, Form, Row, Col } from "antd";
import debounce from "lodash.debounce";
import api from "../../../../axiosConfig";
import dayjs from "../../../../utils/dayjs";
import { useUser } from "../../../../UserContext";

import {
  UserAddOutlined,
  TeamOutlined,
  SafetyOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

// Componentes hijos
import PatientInfo from "../Form/PatientInfo";
import ResponsibleInfo from "../Form/ResponsibleInfo";
import AuthorizationData from "../Form/AuthorizationData";
import AuthorizationDates from "../Form/AuthorizationDates";
import GeografiaAutorizacion from "../Form/GeografiaAutorizacion";

const { TextArea } = Input;
const { Title } = Typography;

// ------------------
// Códigos especiales
// ------------------
const SPECIAL_CUPS_CODES = ["FS33303", "FS33305"];
const INTERMUNICIPAL_CODES = [
  "FS31324",
  "FS31325",
  "FS31326",
  "FS31327",
  "FS31355",
  "FS31356",
  "FS31357",
  "FS31358",
];

// ------------------
// Estado inicial
// ------------------
const initialFormState = {
  paciente_id: "",
  nombre_paciente: "",
  tipo_documento_paciente: "",
  numero_documento_paciente: "",
  genero: "",
  departamento: "",
  ciudad: "",
  empresa: "",
  regimen: "",
  observaciones: "",
  nombre_responsable: "",
  parentesco_responsable: "",
  telefono_responsable: "",
  codigo_mipres: "",
  cups_codigo: "",
  cups_descripcion: "",
  tipo_viaje: "",
  tipo_traslado: "",
  numero_entrega: 1,
  entregas_autorizadas: 1,
  numero_autorizacion: "",
  numero_pre_autorizacion: "",
  proposito_id: "",
  proposito: "",
  tipo_autorizacion: "abierta",
  inicio_fecha_autorizacion: null,
  final_fecha_autorizacion: null,
  estado_autorizacion: "Activa",
  categoria_autorizacion: "",
  total_traslados_asignados: "",
  kilometros_autorizados: "",
  kilometros_por_servicios: "",
  copago: 0,
  porcentaje_copago_selected: 0,
  copago_diario: 0,
  valor_maximo_copago: null,
  // ORIGEN
  origen_institucion: "",
  origen_coordenadas: null,
  origen_departamento: "",
  origen_ciudad: "",
  origen_localidad: "",
  origen_barrio: "",
  origen_vereda_corregimiento: "",
  origen_direccion: "",
  origen_indicaciones: "",
  // DESTINO
  destino_institucion: "",
  destino_coordenadas: null,
  destino_departamento: "",
  destino_ciudad: "",
  destino_localidad: "",
  destino_barrio: "",
  destino_vereda_corregimiento: "",
  destino_direccion: "",
  destino_indicaciones: "",
  // Otros
  no_pbs: true,
  calendario: [],
  crear_nuevo_mipres: false,
  // Campos TAI
  es_tai: false,
  tai_codigo: [],  // Array de códigos CUPS TAI seleccionados
  coberturas: "",  // Coberturas separadas por coma
  transportadora_id: null,
  cliente_tai_id: null,  // ID del cliente TAI seleccionado
  tai_tipo_transporte: null,
  tai_configuracion: null,
  con_acompanante: false,  // Solo para TAI Intermunicipal
  tiene_intermunicipal: false, // True si algún CUPS seleccionado es Intermunicipal
};

// ------------------
// Funciones puras (ID <-> Texto, etc.)
// ------------------

// (1) NOMBRE -> ID
function findDepartamentoIdByName(name, departamentos) {
  if (!name || !Array.isArray(departamentos)) return "";
  const dep = departamentos.find((d) => d.nombre === name);
  return dep ? dep.id : "";
}
function findInstitucionIdByName(name, instituciones) {
  if (!name || !Array.isArray(instituciones)) return "";
  const inst = instituciones.find((i) => i.nombre === name);
  return inst ? inst.id : "";
}

// (2) ID -> NOMBRE
function getDepartmentName(id, departamentos) {
  if (!id || !Array.isArray(departamentos)) return "";
  const dep = departamentos.find((d) => d.id === id);
  return dep ? dep.nombre : "";
}
function getCityName(cityId, ciudades) {
  if (!cityId || !Array.isArray(ciudades)) return "";
  const c = ciudades.find((ciudad) => ciudad.id === cityId);
  return c ? c.nombre : "";
}
function getInstitutionName(instId, instituciones) {
  if (!instId || !Array.isArray(instituciones)) return "";
  const i = instituciones.find((inst) => inst.id === instId);
  return i ? i.nombre : "";
}

// (3) Otras utilidades - parsear coordenadas a formato user-friendly
function parsePoint(geom) {
  if (!geom || !geom.coordinates) return "";
  const [lng, lat] = geom.coordinates;
  return `${lat}, ${lng}`;
}

// (4) Obtener datos de paciente
function getDatosOrigenPaciente(patient) {
  // La nueva API ya retorna las coordenadas en formato string "lat, lng"
  let coordenadas = "";
  if (patient.coordenadas) {
    if (typeof patient.coordenadas === 'string') {
      coordenadas = patient.coordenadas; // Ya está en formato "lat, lng"
    } else if (
      patient.coordenadas &&
      patient.coordenadas.coordinates &&
      Array.isArray(patient.coordenadas.coordinates)
    ) {
      // GeoJSON es [lng, lat], pero frontend necesita "lat, lng"
      const lng = patient.coordenadas.coordinates[0];
      const lat = patient.coordenadas.coordinates[1];
      coordenadas = `${lat}, ${lng}`;
    }
  }
  return {
    origen_departamento: patient.departamento_id,
    origen_ciudad: patient.ciudad_id,
    origen_localidad: patient.localidad || "",
    origen_barrio: patient.barrio || "",
    origen_vereda_corregimiento: patient.vereda_corregimiento || "",
    origen_direccion: patient.direccion || "",
    origen_indicaciones: patient.indicaciones || "",
    origen_coordenadas: coordenadas,
  };
}
function getBasicPatientData(patient) {
  return {
    paciente_id: patient.id,
    nombre_paciente: patient.nombre,
    tipo_documento_paciente: patient.tipo_documento?.descripcion || "",
    numero_documento_paciente: patient.numero_documento || "",
    genero: patient.genero || "",
    fecha_nacimiento: patient.fecha_nacimiento || "",
  };
}
function getAdditionalPatientData(patient) {
  return {
    departamento: patient.ciudad?.departamento?.nombre || patient.departamento?.nombre || "",
    ciudad: patient.ciudad?.nombre || "",
    empresa: patient.cliente?.nombre_cliente || "",
    regimen: patient.regimen?.nombre || "",
    nombre_responsable: patient.nombre_responsable || "",
    parentesco_responsable: patient.parentesco_responsable || "",
    telefono_responsable: patient.telefono_responsable || "",
    diagnostico_nombre: patient.diagnostico?.nombre || "",
  };
}
function getPatientFormData(patient) {
  const origen = getDatosOrigenPaciente(patient);
  return {
    ...getBasicPatientData(patient),
    ...getAdditionalPatientData(patient),
    // Origen
    origen_departamento: patient.departamento_id || "",
    origen_ciudad: patient.ciudad_id || "",
    origen_localidad: patient.localidad || "",
    origen_barrio: patient.barrio || "",
    origen_vereda_corregimiento: patient.vereda_corregimiento || "",
    origen_indicaciones: patient.indicaciones || "",
    origen_direccion: patient.direccion || "",
    origen_coordenadas: origen.origen_coordenadas,
    origen_institucion: "", // Limpio
  };
}

// ------------------
// Componente principal
// ------------------
const CreateAuthorizationForm = ({
  handleClose,
  fetchAuthorizations,
  authorization,
}) => {
  const { user } = useUser();

  // Función para verificar permisos de autorizaciones
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.es_administrador && user.sinRol) return true;
    return user.modulosPermisos?.some(
      (mp) => mp.modulo?.toLowerCase() === "autorizaciones" && mp.permiso === permission
    );
  };

  const [form, setForm] = useState(initialFormState);
  const [geografia, setGeografia] = useState(null);
  const [patients, setPatients] = useState([]);
  const [cups, setCups] = useState([]);
  const [propositos, setPropositos] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [errors, setErrors] = useState({
    codigo_mipres: "",
    numero_autorizacion: "",
    numero_pre_autorizacion: "",
  });
  const [isMipresExisting, setIsMipresExisting] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [calendarStartDate, setCalendarStartDate] = useState(null);
  const [ciudadesOrigen, setCiudadesOrigen] = useState([]);
  const [ciudadesDestino, setCiudadesDestino] = useState([]);
  const [datosOrigenPaciente, setDatosOrigenPaciente] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copagoFieldsDisabled, setCopagoFieldsDisabled] = useState(false);
  const [availableEntregas, setAvailableEntregas] = useState([]);

  // Estado para clientes TAI
  const [clientesTai, setClientesTai] = useState([]);
  const [loadingClientesTai, setLoadingClientesTai] = useState(false);
  const [empresaOriginalPaciente, setEmpresaOriginalPaciente] = useState("");

  //
  // =========================================================
  // FETCH & useEffects
  // =========================================================
  //
  const fetchDepartamentos = async () => {
    try {
      const resp = await api.get("/departamentos");
      setDepartamentos(resp.data);
    } catch (err) {
      console.error("Error al cargar departamentos:", err);
      message.error("Error al cargar departamentos.");
    }
  };
  const fetchInstituciones = async () => {
    try {
      const resp = await api.get("/instituciones");
      setInstituciones(resp.data);

      // Modo creación: poner "Sin Destino" por defecto
      if (!authorization) {
        const sinDestino = resp.data.find(
          (inst) => inst.nombre.toLowerCase() === "sin destino"
        );
        if (sinDestino) {
          updateFormWithInstitucionData(sinDestino, "destino");
        }
      }
    } catch (err) {
      console.error("Error al cargar instituciones:", err);
      message.error("Error al cargar instituciones.");
    }
  };
  const fetchPatients = async () => {
    try {
      // Usar la nueva API optimizada para el selector
      const resp = await api.get("/pacientes/selector");
      setPatients(resp.data || []);
    } catch (err) {
      console.error("Error al cargar pacientes:", err);
      message.error("Error al cargar pacientes.");
      setPatients([]);
    }
  };
  const fetchPropositos = async () => {
    try {
      const resp = await api.get("/propositos");
      setPropositos(resp.data);
    } catch (err) {
      console.error("Error al cargar propósitos:", err);
      message.error("Error al cargar propósitos.");
    }
  };
  const fetchCiudades = async (departamentoId, setCiudades) => {
    if (!departamentoId) return;
    try {
      const resp = await api.get(`/ciudades?departamento_id=${departamentoId}`);
      setCiudades(resp.data);
    } catch (err) {
      console.error("Error al cargar ciudades:", err);
      message.error("Error al cargar ciudades.");
    }
  };
  const fetchCupsByCliente = async (clienteId) => {
    try {
      const resp = await api.get(`/cups/cliente/${clienteId}`);
      setCups(resp.data);
    } catch (err) {
      console.error("Error al cargar CUPS:", err);
      message.error("Error al cargar CUPS.");
    }
  };

  // Cargar clientes TAI (filtrados por EPS del paciente si existe)
  const fetchClientesTai = async (epsId = null) => {
    try {
      setLoadingClientesTai(true);
      // Si hay un EPS ID, obtener solo los clientes TAI relacionados a esa EPS
      const endpoint = epsId ? `/clientes/tai/by-eps/${epsId}` : "/clientes/tai";
      const resp = await api.get(endpoint);
      setClientesTai(resp.data);
    } catch (err) {
      console.error("Error al cargar clientes TAI:", err);
      message.error("Error al cargar clientes TAI.");
    } finally {
      setLoadingClientesTai(false);
    }
  };

  // useEffect principal (montado)
  useEffect(() => {
    // Cargar datos
    fetchDepartamentos();
    fetchInstituciones();
    fetchPatients();
    fetchPropositos();

    if (authorization) {
      // Modo edición

      // 1) Parsear calendario
      let parsedCal = [];
      if (typeof authorization.calendario === "string") {
        parsedCal = JSON.parse(authorization.calendario);
      } else if (Array.isArray(authorization.calendario)) {
        parsedCal = authorization.calendario;
      }

      // 2) Configurar estado para selectedDays y dateRange
      setSelectedDays(
        (parsedCal || []).map((d) => dayjs(d).format("YYYY-MM-DD"))
      );
      setDateRange([
        authorization.inicio_fecha_autorizacion
          ? dayjs(authorization.inicio_fecha_autorizacion)
          : null,
        authorization.final_fecha_autorizacion
          ? dayjs(authorization.final_fecha_autorizacion)
          : null,
      ]);
      setCalendarStartDate(
        authorization.inicio_fecha_autorizacion
          ? dayjs(authorization.inicio_fecha_autorizacion)
          : null
      );

      // 3) Set form con la data de la autorización
      setForm((prev) => ({
        ...prev,
        ...authorization,
        inicio_fecha_autorizacion: authorization.inicio_fecha_autorizacion
          ? dayjs(authorization.inicio_fecha_autorizacion).format("YYYY-MM-DD")
          : null,
        final_fecha_autorizacion: authorization.final_fecha_autorizacion
          ? dayjs(authorization.final_fecha_autorizacion).format("YYYY-MM-DD")
          : null,
        calendario: parsedCal,
        origen_coordenadas: parsePoint(authorization.origen_coordenadas),
        destino_coordenadas: parsePoint(authorization.destino_coordenadas),
      }));

      // 4) Validar MIPRES si existe
      if (authorization.codigo_mipres) {
        validateMipresCode(
          authorization.codigo_mipres,
          authorization.paciente_id
        );
      }
    } else {
      // Modo creación
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorization]);

  // useEffect para convertir TEXTO -> ID en MODO EDICION
  useEffect(() => {
    if (authorization && departamentos.length && instituciones.length) {
      const convertToIds = () => {
        setForm((prevForm) => {
          const origDepId = findDepartamentoIdByName(
            authorization.origen_departamento,
            departamentos
          );
          const destDepId = findDepartamentoIdByName(
            authorization.destino_departamento,
            departamentos
          );
          const origInstId = findInstitucionIdByName(
            authorization.origen_institucion,
            instituciones
          );
          const destInstId = findInstitucionIdByName(
            authorization.destino_institucion,
            instituciones
          );

          return {
            ...prevForm,
            origen_departamento: origDepId,
            destino_departamento: destDepId,
            origen_institucion: origInstId,
            destino_institucion: destInstId,
          };
        });
      };
      convertToIds();
    }
  }, [authorization, departamentos, instituciones]);

  //
  // =========================================================
  // RESET FORM
  // =========================================================
  //
  const resetForm = () => {
    setForm(initialFormState);
    setSelectedDays([]);
    setDateRange([null, null]);
    setCalendarStartDate(null);
    setIsMipresExisting(false);
    setAvailableEntregas([]);
    setErrors({
      codigo_mipres: "",
      numero_autorizacion: "",
      numero_pre_autorizacion: "",
    });
    setDatosOrigenPaciente(null);
    setCopagoFieldsDisabled(false);
  };

  //
  // =========================================================
  // CÁLCULOS (kilómetros, copago, etc.)
  // =========================================================
  //
  // Calcular km por servicios
  const calculateKilometrosPorServicios = () => {
    const { total_traslados_asignados, kilometros_autorizados, tipo_viaje } =
      form;
    if (
      total_traslados_asignados !== undefined &&
      total_traslados_asignados !== null &&
      kilometros_autorizados !== undefined &&
      kilometros_autorizados !== null &&
      tipo_viaje &&
      total_traslados_asignados > 0
    ) {
      const isRedondo = tipo_viaje.toLowerCase() === "redondo";
      const kmPorServicio = isRedondo
        ? kilometros_autorizados / (total_traslados_asignados * 2)
        : kilometros_autorizados / total_traslados_asignados;

      setForm((prev) => ({
        ...prev,
        kilometros_por_servicios: parseFloat(kmPorServicio.toFixed(2)),
      }));
    } else if (kilometros_autorizados === 0 || total_traslados_asignados === 0) {
      // Si alguno es 0, el resultado debe ser 0
      setForm((prev) => ({
        ...prev,
        kilometros_por_servicios: 0,
      }));
    }
  };
  useEffect(() => {
    calculateKilometrosPorServicios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.total_traslados_asignados,
    form.kilometros_autorizados,
    form.tipo_viaje,
  ]);

  // Manejo del selectedDays
  useEffect(() => {
    if (selectedDays && selectedDays.length > 0) {
      const minTimestamp = Math.min(
        ...(selectedDays || []).map((date) => dayjs(date).valueOf())
      );
      setCalendarStartDate(dayjs(minTimestamp));
    } else {
      setCalendarStartDate(null);
    }
  }, [selectedDays]);

  // Calcular copago
  const calculateCopago = () => {
    if (!copagoFieldsDisabled) return; // Solo se calcula si la lógica lo requiere
    const selectedCups = cups.find((c) => c.codigo === form.cups_codigo);
    if (!selectedCups) return;

    const { codigo } = selectedCups;
    const porcentaje = parseFloat(form.porcentaje_copago_selected) || 0;
    const total = parseInt(form.total_traslados_asignados, 10) || 1;
    const valorMax = parseFloat(form.valor_maximo_copago) || null;

    let copagoTotal = 0;
    let copagoDiario = 0;

    // Si es un cups especial
    if (SPECIAL_CUPS_CODES.includes(codigo)) {
      const valorTraslado = parseFloat(selectedCups.valor_traslado) || 0;
      copagoTotal = valorTraslado * (porcentaje / 100) * total;
      copagoDiario = valorTraslado * (porcentaje / 100);

      if (valorMax && copagoTotal > valorMax) {
        copagoTotal = valorMax;
        copagoDiario = copagoTotal / total;
      }
    } else if (INTERMUNICIPAL_CODES.includes(codigo)) {
      const kmMax = parseFloat(selectedCups.kilometraje_maximo) || 0;
      const valorKm = 3600; // Ejemplo
      copagoTotal = kmMax * valorKm * (porcentaje / 100) * total;
      copagoDiario = kmMax * valorKm * (porcentaje / 100);

      if (valorMax && copagoTotal > valorMax) {
        copagoTotal = valorMax;
        copagoDiario = copagoTotal / total;
      }
    } else {
      // Para otros cups
      copagoTotal = form.copago;
      copagoDiario = copagoTotal / total;
    }

    setForm((prev) => ({
      ...prev,
      copago: parseFloat(copagoTotal.toFixed(2)),
      copago_diario: parseFloat(copagoDiario.toFixed(2)),
    }));
  };
  useEffect(() => {
    calculateCopago();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.cups_codigo,
    form.porcentaje_copago_selected,
    form.total_traslados_asignados,
    form.valor_maximo_copago,
    copagoFieldsDisabled,
    cups,
  ]);

  //
  // =========================================================
  // HANDLERS
  // =========================================================
  //
  // handleDaySelection
  const handleDaySelection = (dayjsDate) => {
    if (!form.inicio_fecha_autorizacion || !form.final_fecha_autorizacion) {
      message.error("Seleccione primero las fechas de inicio y fin.");
      return;
    }
    const inicio = dayjs(form.inicio_fecha_autorizacion).startOf("day");
    const fin = dayjs(form.final_fecha_autorizacion).endOf("day");
    if (dayjsDate.isBefore(inicio) || dayjsDate.isAfter(fin)) {
      message.error("El día está fuera del rango seleccionado.");
      return;
    }
    const dayString = dayjsDate.format("YYYY-MM-DD");
    let updated = [...selectedDays];
    if (updated.includes(dayString)) {
      // Quitar el día si ya está
      updated = updated.filter((d) => d !== dayString);
    } else {
      updated.push(dayString);
    }
    setSelectedDays(updated);
    setForm((prev) => ({ ...prev, calendario: updated }));
  };

  // Intermunicipal => recalcular kmAut
  useEffect(() => {
    if (INTERMUNICIPAL_CODES.includes(form.cups_codigo)) {
      const total = parseInt(form.total_traslados_asignados, 10) || 1;
      const kmMax = parseFloat(form.kilometraje_maximo) || 0;
      const kmAut = total * 2 * kmMax;
      setForm((prev) => ({
        ...prev,
        kilometros_autorizados: kmAut,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.total_traslados_asignados,
    form.cups_codigo,
    form.kilometraje_maximo,
  ]);

  // Debounce
  const useDebouncedCallback = (callback, delay) => {
    const debounced = useRef(debounce(callback, delay));
    return debounced.current;
  };

  // Validar unicidad de # de autorización / pre-autorización
  const validateUniqueFields = useDebouncedCallback(
    async (numeroAut, numeroPreAut) => {
      if (!numeroAut && !numeroPreAut) return;
      try {
        const resp = await api.post("/autorizaciones/verify-fields", {
          numero_autorizacion: numeroAut,
          numero_pre_autorizacion: numeroPreAut,
        });
        if (resp.data.exists && resp.data.conflictMap) {
          setErrors((prev) => ({
            ...prev,
            numero_autorizacion:
              resp.data.conflictMap.numero_autorizacion || "",
            numero_pre_autorizacion:
              resp.data.conflictMap.numero_pre_autorizacion || "",
          }));
        } else {
          // Campos OK
          setErrors((prev) => ({
            ...prev,
            numero_autorizacion: "",
            numero_pre_autorizacion: "",
          }));
        }
      } catch (err) {
        console.error("Error al verificar unicidad:", err);
        setErrors((prev) => ({
          ...prev,
          numero_autorizacion: "Error al verificar la autorización.",
          numero_pre_autorizacion: "Error al verificar la pre-autorización.",
        }));
      }
    },
    500
  );
  // Cancelar debounce en cleanup
  useEffect(() => {
    return () => {
      validateUniqueFields.cancel();
    };
  }, [validateUniqueFields]);

  // onChange general
  const handleChange = (e) => {
    const { name, value } = e.target;
    const specialHandlers = {
      origen_departamento: handleOrigenDepartamentoChange,
      destino_departamento: handleDestinoDepartamentoChange,
      codigo_mipres: handleCodigoMipresChange,
      tipo_autorizacion: handleTipoAutorizacionChange,
    };

    if (specialHandlers[name]) {
      specialHandlers[name](value);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));

      // Validar unicidad #aut / #preAut
      if (
        name === "numero_autorizacion" ||
        name === "numero_pre_autorizacion"
      ) {
        validateUniqueFields(
          name === "numero_autorizacion" ? value : form.numero_autorizacion,
          name === "numero_pre_autorizacion"
            ? value
            : form.numero_pre_autorizacion
        );
      }

      // Validar copago_diario <= copago
      if (name === "copago" || name === "copago_diario") {
        const copagoVal = parseFloat(name === "copago" ? value : form.copago) || 0;
        const copagoDiarioVal = parseFloat(name === "copago_diario" ? value : form.copago_diario) || 0;
        if (copagoDiarioVal > copagoVal) {
          setErrors((prev) => ({ ...prev, copago_diario: "El Copago por Servicios no puede ser mayor al Copago Total." }));
        } else {
          setErrors((prev) => ({ ...prev, copago_diario: "" }));
        }
      }
    }
    // Limpia error si se corrige
    if (name === "codigo_mipres" && errors.codigo_mipres) {
      setErrors((prev) => ({ ...prev, codigo_mipres: "" }));
    }
  };

  // check No PBS
  const handleNoPbsChange = (checked) => {
    setForm((prev) => ({
      ...prev,
      no_pbs: checked,
      codigo_mipres: checked ? "" : prev.codigo_mipres,
      numero_entrega: 1,
      crear_nuevo_mipres: false,
      entregas_autorizadas: checked ? 1 : prev.entregas_autorizadas,
    }));
    if (checked) {
      setErrors((prev) => ({ ...prev, codigo_mipres: "" }));
      setIsMipresExisting(false);
      setAvailableEntregas([]);
      setForm((prev) => ({ ...prev, entregas_autorizadas: 1 }));
    }
  };

  // TAI Switch handler
  const handleTaiChange = (checked) => {
    if (checked) {
      // Al activar TAI:
      // 1. Guardar la empresa original del paciente
      setEmpresaOriginalPaciente(form.empresa || "");
      // 2. Limpiar la empresa y CUPS para que seleccione una de las TAI
      // TAI siempre es NO PBS y tipo autorización Cerrada
      setForm((prev) => ({
        ...prev,
        es_tai: true,
        no_pbs: true, // TAI siempre es NO PBS
        tipo_autorizacion: "cerrada", // TAI por defecto tipo cerrada
        codigo_mipres: "", // Limpiar MIPRES ya que es NO PBS
        empresa: "", // Limpiar empresa para seleccionar cliente TAI
        cups_codigo: "",
        cups_descripcion: "",
        tipo_viaje: "",
        tipo_traslado: "",
        tai_codigo: [],
        coberturas: "",
        transportadora_id: null,
        cliente_tai_id: null,
        tai_tipo_transporte: null,
        tai_configuracion: null,
      }));
      // Limpiar errores de MIPRES
      setErrors((prev) => ({ ...prev, codigo_mipres: "" }));
      setIsMipresExisting(false);
      setAvailableEntregas([]);
      // 3. Limpiar CUPS (se cargarán al seleccionar cliente TAI)
      setCups([]);
      // 4. Cargar clientes TAI basados en la EPS del paciente
      const selectedPatient = patients.find((p) => p.id === form.paciente_id);
      if (selectedPatient && selectedPatient.eps) {
        // Cargar clientes TAI relacionados con la EPS del paciente
        fetchClientesTai(selectedPatient.eps);
      } else {
        // Si no hay paciente o no tiene EPS, cargar todos los clientes TAI
        fetchClientesTai();
      }
    } else {
      // Al desactivar TAI:
      // 1. Restaurar la empresa original del paciente
      setForm((prev) => ({
        ...prev,
        es_tai: false,
        empresa: empresaOriginalPaciente || prev.empresa,
        cups_codigo: "",
        cups_descripcion: "",
        tipo_viaje: "",
        tipo_traslado: "",
        tai_codigo: [],
        coberturas: "",
        transportadora_id: null,
        cliente_tai_id: null,
        tai_tipo_transporte: null,
        tai_configuracion: null,
      }));
      // 2. Recargar CUPS del paciente original
      if (form.paciente_id) {
        const selectedPatient = patients.find((p) => p.id === form.paciente_id);
        if (selectedPatient && selectedPatient.eps) {
          fetchCupsByCliente(selectedPatient.eps);
        }
      }
    }
  };

  // Handler para cambio de empresa TAI
  const handleEmpresaTaiChange = (value) => {
    // Buscar el cliente TAI por nombre_cliente para obtener su ID
    const clienteTai = clientesTai.find((c) => c.nombre_cliente === value);

    // Limpiar CUPS actuales y cargar los del cliente TAI
    setForm((prev) => ({
      ...prev,
      empresa: value,
      cliente_tai_id: clienteTai?.id || null, // Guardar el ID del cliente TAI
      cups_codigo: "",
      cups_descripcion: "",
      tipo_viaje: "",
      tipo_traslado: "",
      tai_codigo: [],
      coberturas: "",
    }));

    // Cargar CUPS del cliente TAI seleccionado
    if (clienteTai && clienteTai.id) {
      fetchCupsByCliente(clienteTai.id);
    } else {
      setCups([]);
    }
  };

  // Handler para selección múltiple de CUPS TAI
  const handleTaiCodigoChange = (selectedCodes) => {
    // Obtener los CUPS seleccionados
    const selectedCups = cups.filter((c) => selectedCodes.includes(c.codigo));

    // Calcular coberturas (separadas por coma)
    const coberturas = selectedCups
      .map((c) => c.cobertura)
      .filter((c) => c)
      .join(", ");

    // Calcular tipo de traslado automáticamente
    const tiposTraslado = [...new Set(selectedCups.map((c) => c.tipo_traslado).filter((t) => t))];
    let tipoTrasladoCalculado = "";

    if (tiposTraslado.length === 1) {
      tipoTrasladoCalculado = tiposTraslado[0];
    } else if (tiposTraslado.length > 1) {
      tipoTrasladoCalculado = "Mixto";
    }

    // Verificar si algún CUPS seleccionado es Intermunicipal
    const tieneIntermunicipal = selectedCups.some(
      (c) => c.tipo_traslado?.toLowerCase() === "intermunicipal"
    );

    // Tipo de viaje: tomar del primer CUPS seleccionado (solo hay Sencillo o Redondo)
    const tipoViajeCalculado = selectedCups.length > 0 ? (selectedCups[0].tipo_viaje || "") : "";

    setForm((prev) => ({
      ...prev,
      tai_codigo: selectedCodes,
      coberturas: coberturas,
      tipo_traslado: tipoTrasladoCalculado,
      tipo_viaje: tipoViajeCalculado,
      // También actualizar cups_codigo y cups_descripcion para compatibilidad
      cups_codigo: selectedCodes.join(", "),
      cups_descripcion: selectedCups.map((c) => c.descripcion).join(" | "),
      // Indicar si hay algún CUPS Intermunicipal seleccionado
      tiene_intermunicipal: tieneIntermunicipal,
    }));
  };

  // TAI Transportadora change
  const handleTransportadoraChange = (value) => {
    setForm((prev) => ({ ...prev, transportadora_id: value }));
  };

  // TAI Con Acompañante change
  const handleConAcompananteChange = (checked) => {
    setForm((prev) => ({ ...prev, con_acompanante: checked }));
  };

  // Cambiar paciente
  const handlePatientChange = async (value) => {
    // 1) Si NO hay paciente seleccionado (limpia los campos)
    if (!value) {
      setForm((prev) => ({
        ...prev,
        paciente_id: "",
        cups_codigo: "",
        cups_descripcion: "",
        tipo_viaje: "",
        tipo_traslado: "",
        porcentaje_copago_selected: null,
        valor_maximo_copago: null,
        copago: 0,
        copago_diario: 0,
      }));
      setEmpresaOriginalPaciente("");
      return;
    }

    try {
      // 2) Cargar los datos completos del paciente seleccionado usando la nueva API
      const resp = await api.get(`/pacientes/${value}/datos-completos`);
      const selectedPatient = resp.data;

      // Validar que el paciente no esté fallecido
      if (selectedPatient.estado?.descripcion?.toLowerCase().includes('fallecid')) {
        message.error('No se puede crear autorización para un paciente en estado fallecido');
        setForm((prev) => ({
          ...prev,
          paciente_id: "",
        }));
        return;
      }

      // 3) Procesar los datos del paciente
      setDatosOrigenPaciente(getDatosOrigenPaciente(selectedPatient));
      await fetchCiudades(selectedPatient.departamento_id, setCiudadesOrigen);
      const pForm = getPatientFormData(selectedPatient);

      // 4) Guardar la empresa original del paciente
      const empresaPaciente = selectedPatient.cliente?.nombre_cliente || "";
      setEmpresaOriginalPaciente(empresaPaciente);

      // 5) Actualizar el formulario con los datos del paciente
      // Si está en modo TAI, NO sobrescribir la empresa seleccionada
      setForm((prev) => ({
        ...prev,
        ...pForm,
        // Si es TAI, mantener la empresa ya seleccionada; si no, usar la del paciente
        empresa: prev.es_tai ? prev.empresa : pForm.empresa,
        cups_codigo: "",
        cups_descripcion: "",
        tipo_viaje: "",
        tipo_traslado: "",
        porcentaje_copago_selected: null,
        valor_maximo_copago: null,
        copago: 0,
        copago_diario: 0,
      }));

      // 6) Cargar CUPS según la EPS (si existe)
      if (selectedPatient.eps) {
        fetchCupsByCliente(selectedPatient.eps);
      } else {
        setCups([]);
      }

      // 7) Si está en modo TAI, recargar clientes TAI basados en la nueva EPS del paciente
      if (form.es_tai) {
        if (selectedPatient.eps) {
          fetchClientesTai(selectedPatient.eps);
        } else {
          // Si el nuevo paciente no tiene EPS, limpiar clientes TAI
          setClientesTai([]);
        }
        // Limpiar la empresa TAI seleccionada ya que cambió el paciente
        setForm((prev) => ({
          ...prev,
          empresa: "",
          tai_codigo: [],
          cups_codigo: "",
          cups_descripcion: "",
          tipo_viaje: "",
          coberturas: "",
        }));
      }
    } catch (err) {
      console.error("Error al cargar datos del paciente:", err);
      message.error("Error al cargar los datos del paciente.");
    }
  };

  // CUPS
  const handleCupsChange = (value) => {
    const selected = cups.find((c) => c.codigo === value);
    if (!selected) {
      // Reset
      setForm((prev) => ({
        ...prev,
        cups_codigo: "",
        cups_descripcion: "",
        tipo_viaje: "",
        tipo_traslado: "",
        porcentaje_copago_selected: null,
        copago: 0,
        copago_diario: 0,
        valor_maximo_copago: null,
        kilometros_autorizados: "",
      }));
      setCopagoFieldsDisabled(false);
      return;
    }
    // Set info
    setForm((prev) => ({
      ...prev,
      cups_codigo: selected.codigo,
      cups_descripcion: selected.descripcion,
      tipo_viaje: selected.tipo_viaje,
      tipo_traslado: selected.tipo_traslado,
      valor_traslado: selected.valor_traslado || 0,
      kilometraje_maximo: selected.kilometraje_maximo || null,
    }));
    // Lógica SPECIAL
    if (
      SPECIAL_CUPS_CODES.includes(selected.codigo) ||
      INTERMUNICIPAL_CODES.includes(selected.codigo)
    ) {
      setCopagoFieldsDisabled(true);
      setForm((prev) => ({ ...prev, porcentaje_copago_selected: 0 }));
    } else {
      setCopagoFieldsDisabled(false);
      setForm((prev) => ({
        ...prev,
        porcentaje_copago_selected: null,
        copago: 0,
        copago_diario: 0,
        valor_maximo_copago: null,
      }));
    }
    // Cálculo intermunicipal
    if (INTERMUNICIPAL_CODES.includes(selected.codigo)) {
      const total = parseInt(form.total_traslados_asignados, 10) || 1;
      const kmMax = parseFloat(selected.kilometraje_maximo) || 0;
      const kmAut = total * 2 * kmMax;
      setForm((prev) => ({ ...prev, kilometros_autorizados: kmAut }));
    }
  };

  // Proposito
  const handlePropositoChange = (value) => {
    const sel = propositos.find((p) => p.descripcion === value);
    if (!sel) {
      setForm((prev) => ({ ...prev, proposito_id: "", proposito: "" }));
    } else {
      setForm((prev) => ({
        ...prev,
        proposito_id: sel.id,
        proposito: sel.descripcion,
      }));
    }
  };

  // Tipo Autorización
  const handleTipoAutorizacionChange = (value) => {
    setForm((prev) => ({
      ...prev,
      tipo_autorizacion: value,
      calendario: [],
    }));
    setSelectedDays([]);
  };

  // Depto ORIGEN
  const handleOrigenDepartamentoChange = (value) => {
    setForm((prev) => ({
      ...prev,
      origen_departamento: value,
      origen_ciudad: "",
    }));
    fetchCiudades(value, setCiudadesOrigen);
  };

  // Depto DESTINO
  const handleDestinoDepartamentoChange = (value) => {
    setForm((prev) => ({
      ...prev,
      destino_departamento: value,
      destino_ciudad: "",
    }));
    fetchCiudades(value, setCiudadesDestino);
  };

  // MIPRES
  const handleCodigoMipresChange = (value) => {
    setForm((prev) => ({ ...prev, codigo_mipres: value }));
    if (value.length === 20) {
      validateMipresCode(value, form.paciente_id);
    } else {
      setIsMipresExisting(false);
      setAvailableEntregas([]);
      setForm((prev) => ({
        ...prev,
        crear_nuevo_mipres: false,
        entregas_autorizadas: 1,
      }));
    }
  };

  // Inst fetch
  const fetchInstitucion = async (id) => {
    const resp = await api.get(`/instituciones/${id}`);
    return resp.data;
  };

  // Actualizar form con datos de la institución
  const updateFormWithInstitucionData = (institucion, type) => {
    const {
      departamento_id,
      ciudad_id,
      localidad,
      barrio,
      direccion,
      coordenadas,
      indicaciones,
    } = institucion;
    const coords = coordenadas || "";
    setForm((prev) => ({
      ...prev,
      [`${type}_institucion`]: institucion.id,
      [`${type}_departamento`]: departamento_id,
      [`${type}_ciudad`]: ciudad_id,
      [`${type}_localidad`]: localidad || "",
      [`${type}_barrio`]: barrio || "",
      [`${type}_direccion`]: direccion || "",
      [`${type}_coordenadas`]: coords,
      [`${type}_indicaciones`]: indicaciones || "",
    }));
  };

  // Cambiar institución
  const handleInstitucionChange = async (value, type) => {
    if (!value) {
      if (type === "origen" && datosOrigenPaciente) {
        // Quitar inst origen => volver a datos del Paciente
        setForm((prev) => ({
          ...prev,
          origen_institucion: "",
          ...datosOrigenPaciente,
        }));
      } else {
        // Quitar inst => vaciar
        setForm((prev) => ({
          ...prev,
          [`${type}_institucion`]: "",
          [`${type}_departamento`]: "",
          [`${type}_ciudad`]: "",
          [`${type}_localidad`]: "",
          [`${type}_barrio`]: "",
          [`${type}_direccion`]: "",
          [`${type}_coordenadas`]: "",
          [`${type}_indicaciones`]: "",
        }));
      }
      return;
    }
    // Buscar
    try {
      const inst = await fetchInstitucion(value);
      await fetchCiudades(
        inst.departamento_id,
        type === "origen" ? setCiudadesOrigen : setCiudadesDestino
      );
      updateFormWithInstitucionData(inst, type);
    } catch (err) {
      console.error("Error fetchInstitucion:", err);
      message.error("Error al obtener la institución seleccionada.");
    }
  };

  // MIPRES
  const handleMaxEntregasReached = () => {
    setErrors((prev) => ({
      ...prev,
      codigo_mipres: "El MIPRES alcanzó el máximo de entregas",
    }));
    message.error("El MIPRES alcanzó el máximo de entregas.");
    setIsMipresExisting(true);
  };
  const validateMipresCode = async (codigo, pacienteId) => {
    try {
      const url = `/mipres/${codigo}/entregas-disponibles`;
      const resp = await api.get(url);
      const { exists, availableEntregas, entregas_autorizadas } = resp.data;
      if (!exists) {
        handleMipresNotFound();
        return;
      }
      if (!authorization && availableEntregas.length === 0) {
        handleMaxEntregasReached();
        return;
      }
      setAvailableEntregas(availableEntregas);
      setIsMipresExisting(true);
      setForm((prev) => ({
        ...prev,
        numero_entrega: availableEntregas[0],
        entregas_autorizadas: entregas_autorizadas,
        crear_nuevo_mipres: true,
      }));
    } catch (err) {
      console.error("Error al validar MIPRES:", err);
      message.error("Error al validar el código MIPRES.");
    }
  };
  const handleMipresNotFound = () => {
    setIsMipresExisting(false);
    setAvailableEntregas([]);
    setForm((prev) => ({
      ...prev,
      entregas_autorizadas: 1,
      crear_nuevo_mipres: true,
    }));
    message.info("El código MIPRES no existe. Se creará uno nuevo.");
  };

  //
  // =========================================================
  // PREPARAR DATOS Y ENVIAR (POST/PUT)
  // =========================================================
  //
  const prepareFormData = () => {
    const {
      codigo_mipres,
      no_pbs,
      numero_autorizacion,
      numero_pre_autorizacion,
      porcentaje_copago_selected,
      kilometros_autorizados,
      kilometros_por_servicios,
      origen_institucion,
      destino_institucion,
      origen_institucion_temporal,
      destino_institucion_temporal,
      crear_nuevo_mipres,
      mipres_id,
      ...rest
    } = form;

    // 1) Copiar todo
    const formData = {
      ...rest,
      no_pbs,
      numero_entrega: form.numero_entrega,
      entregas_autorizadas: form.entregas_autorizadas,
      porcentaje_copago_total: porcentaje_copago_selected,
      usuario_id: user.id, // <-- Quién está creando/actualizando
      inicio_fecha_autorizacion: form.inicio_fecha_autorizacion
        ? dayjs(form.inicio_fecha_autorizacion).format("YYYY-MM-DD")
        : null,
      final_fecha_autorizacion: form.final_fecha_autorizacion
        ? dayjs(form.final_fecha_autorizacion).format("YYYY-MM-DD")
        : null,
      calendario: form.calendario,
      ...(no_pbs ? {} : { codigo_mipres }),
      ...(numero_autorizacion ? { numero_autorizacion } : {}),
      ...(numero_pre_autorizacion ? { numero_pre_autorizacion } : {}),
      ...(kilometros_autorizados !== undefined && kilometros_autorizados !== null ? { kilometros_autorizados } : {}),
      ...(kilometros_por_servicios !== undefined && kilometros_por_servicios !== null ? { kilometros_por_servicios } : {}),
      origen_institucion: origen_institucion || null,
      destino_institucion: destino_institucion || null,
      // Si no hay inst, usar lugar_temporal
      origen_lugar_temporal:
        !origen_institucion && origen_institucion_temporal
          ? origen_institucion_temporal
          : null,
      destino_lugar_temporal:
        !destino_institucion && destino_institucion_temporal
          ? destino_institucion_temporal
          : null,
      ...(mipres_id ? { mipres_id } : {}),
      // Campos TAI
      es_tai: form.es_tai || false,
      tai_codigo: form.es_tai && form.tai_codigo?.length > 0
        ? (Array.isArray(form.tai_codigo) ? form.tai_codigo.join(", ") : form.tai_codigo)
        : null,
      coberturas: form.es_tai ? form.coberturas : null,
      transportadora_id: form.es_tai ? form.transportadora_id : null,
      cliente_tai_id: form.es_tai ? form.cliente_tai_id : null,
      // tai_tipo_transporte usa el tipo_traslado calculado de los CUPS
      tai_tipo_transporte: form.es_tai ? form.tipo_traslado : null,
      tai_configuracion: form.es_tai ? form.tai_configuracion : null,
      // con_acompanante - solo relevante para TAI Intermunicipal
      // El checkbox solo aparece cuando es TAI e Intermunicipal, así que enviamos el valor directo
      con_acompanante: form.es_tai ? (form.con_acompanante || false) : false,
    };

    // 2) Convertir ID -> TEXTO en dptos y ciudad
    formData.origen_departamento = getDepartmentName(
      form.origen_departamento,
      departamentos
    );
    formData.origen_ciudad = getCityName(form.origen_ciudad, ciudadesOrigen);
    if (origen_institucion) {
      formData.origen_institucion = getInstitutionName(
        origen_institucion,
        instituciones
      );
    }

    formData.destino_departamento = getDepartmentName(
      form.destino_departamento,
      departamentos
    );
    formData.destino_ciudad = getCityName(form.destino_ciudad, ciudadesDestino);
    if (destino_institucion) {
      formData.destino_institucion = getInstitutionName(
        destino_institucion,
        instituciones
      );
    }

    return formData;
  };

  // Validar MIPRES antes de submit
  const validateMipresBeforeSubmit = async (formData) => {
    if (!formData.no_pbs && formData.codigo_mipres && isMipresExisting) {
      try {
        const resp = await api.get(
          `/mipres/${formData.codigo_mipres}/entregas-disponibles`
        );
        const { exists, availableEntregas } = resp.data;
        if (!exists) {
          setErrors((prev) => ({
            ...prev,
            codigo_mipres: "El código MIPRES no existe.",
          }));
          message.error("El código MIPRES no existe.");
          return false;
        }
        if (availableEntregas.length === 0) {
          setErrors((prev) => ({
            ...prev,
            codigo_mipres: "Se alcanzó el máximo de entregas para este MIPRES.",
          }));
          message.error("Se alcanzó el máximo de entregas para este MIPRES.");
          return false;
        }
        // OK
      } catch (err) {
        console.error("Error al validar MIPRES antes de enviar:", err);
        message.error("Error al validar MIPRES antes de enviar.");
        return false;
      }
    }
    return true;
  };

  // Guardar: si `authorization` existe => PUT, si no => POST
  const saveAuthorization = async (formData) => {
    if (authorization) {
      // MODO EDICION => PUT
      await guardarAutorizacion(authorization.id, formData);
      message.success("Autorización actualizada correctamente.");
    } else {
      // MODO CREACION => POST
      await api.post("/autorizaciones", formData);
      message.success("Autorización creada correctamente.");
    }
  };

  // Submit final
  const handleSubmit = async () => {
    // 1) Revisar errores
    if (
      errors.codigo_mipres ||
      errors.numero_autorizacion ||
      errors.numero_pre_autorizacion ||
      errors.copago_diario
    ) {
      message.error("Corrige los errores en los campos.");
      return;
    }

    if (
      !form.cups_codigo ||
      !form.cups_descripcion ||
      !form.tipo_viaje ||
      !form.tipo_traslado
    ) {
      message.error("Todos los campos de CUPS son obligatorios.");
      return;
    }
    // 2) Al menos un #aut o #pre
    if (!form.numero_autorizacion && !form.numero_pre_autorizacion) {
      message.error("Se requiere # de autorización o # de pre-autorización.");
      return;
    }
    // 3) Validar copago_diario <= copago
    const copagoVal = parseFloat(form.copago) || 0;
    const copagoDiarioVal = parseFloat(form.copago_diario) || 0;
    if (copagoDiarioVal > copagoVal) {
      setErrors((prev) => ({ ...prev, copago_diario: "El Copago por Servicios no puede ser mayor al Copago Total." }));
      message.error("El Copago por Servicios no puede ser mayor al Copago Total.");
      return;
    }

    try {
      setLoading(true);
      // 3) Preparar data
      const formData = prepareFormData();
    // Las ubicaciones se resuelven en el servidor desde el ámbito y tipo de lugar elegidos.
    Object.keys(formData).filter(key => key.startsWith('origen_') || key.startsWith('destino_')).forEach(key => delete formData[key]);
    if (geografia) Object.assign(formData, geografia);
      // 4) Validar MIPRES
      const mipresOk = await validateMipresBeforeSubmit(formData);
      if (!mipresOk) {
        setLoading(false);
        return;
      }
      // 5) Guardar
      await saveAuthorization(formData);
      // 6) Refrescar, cerrar y reset
      fetchAuthorizations();
      handleClose();
      resetForm();
    } catch (error) {
      console.error("Error al enviar formulario:", error);
      
      // Mostrar mensaje específico del servidor si está disponible
      const errorMessage = error?.response?.data?.message || "Error al crear/actualizar la autorización.";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  //
  // =========================================================
  // RENDER
  // =========================================================
  //
  return (
    <Form layout="vertical" onFinish={handleSubmit}>
      <Row gutter={24}>
        {/* ----------- Columna 1: Paciente, Responsable, Fechas ----------- */}
        <Col xs={24} sm={24} md={8}>
          {/* Paciente */}
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <UserAddOutlined style={{ marginRight: 8 }} />
              Información del Paciente
            </Title>
            <PatientInfo
              form={form}
              handleChange={handleChange}
              patients={patients}
              handlePatientChange={handlePatientChange}
              isEditMode={!!authorization}
              // Props para modo TAI
              esTai={form.es_tai}
              clientesTai={clientesTai}
              loadingClientesTai={loadingClientesTai}
              onEmpresaTaiChange={handleEmpresaTaiChange}
              empresaOriginalPaciente={empresaOriginalPaciente}
            />
          </div>

          {/* Responsable */}
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <TeamOutlined style={{ marginRight: 8 }} />
              Información del Responsable
            </Title>
            <ResponsibleInfo form={form} handleChange={handleChange} />
          </div>

          {/* Fechas */}
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <CalendarOutlined style={{ marginRight: 8 }} />
              Fechas de la Autorización
            </Title>
            <AuthorizationDates
              form={form}
              handleChange={handleChange}
              handleDaySelection={handleDaySelection}
              selectedDays={selectedDays}
              dateRange={dateRange}
              setDateRange={setDateRange}
              calendarStartDate={calendarStartDate || dayjs()}
              setCalendarStartDate={setCalendarStartDate}
            />
          </div>
        </Col>

        {/* ----------- Columna 2: Datos de Autorización ----------- */}
        <Col xs={24} sm={24} md={8}>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <SafetyOutlined style={{ marginRight: 8 }} />
              Datos de la Autorización
            </Title>
            <AuthorizationData
              form={form}
              handleChange={handleChange}
              cups={cups}
              handleCupsChange={handleCupsChange}
              errors={errors}
              isMipresExisting={isMipresExisting}
              handleNoPbsChange={handleNoPbsChange}
              propositos={propositos}
              handlePropositoChange={handlePropositoChange}
              isEditMode={false}
              copagoFieldsDisabled={copagoFieldsDisabled}
              availableEntregas={availableEntregas}
              esTai={form.es_tai}
              handleTaiCodigoChange={handleTaiCodigoChange}
              handleTaiChange={handleTaiChange}
              hasPermissionTai={hasPermission("SWITCH_TAI_A")}
              transportadoraId={form.transportadora_id}
              onTransportadoraChange={handleTransportadoraChange}
              conAcompanante={form.con_acompanante}
              onConAcompananteChange={handleConAcompananteChange}
              tieneIntermunicipal={form.tiene_intermunicipal}
            />
          </div>
        </Col>

        {/* ----------- Columna 3: Ubicación/Origen/Destino ----------- */}
        <Col xs={24} sm={24} md={8}>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EnvironmentOutlined style={{ marginRight: 8 }} />
              Origen y destino del traslado
            </Title>
            <GeografiaAutorizacion pacienteId={form.paciente_id} autorizacionId={authorization?.id} onChange={setGeografia} />
          </div>
        </Col>
      </Row>

      {/* Observaciones y TAI */}
      <Row gutter={24}>
        <Col span={16}>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <FileTextOutlined style={{ marginRight: 8 }} />
              Observaciones
            </Title>
            <Form.Item>
              <TextArea
                name="observaciones"
                value={form.observaciones}
                onChange={handleChange}
                placeholder="Observaciones"
                rows={4}
              />
            </Form.Item>
          </div>
        </Col>
      </Row>

      {/* Botones */}
      <Form.Item>
        <Row justify="end">
          <Col>
            <Button
              onClick={() => {
                resetForm();
                handleClose();
              }}
              style={{
                backgroundColor: "#d32f2f",
                color: "white",
                borderColor: "#d32f2f",
                borderRadius: 4,
                marginRight: 8,
                transition: "background-color 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#b71c1c")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#d32f2f")
              }
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                backgroundColor: "#0a2e5c",
                color: "white",
                borderColor: "#0a2e5c",
                borderRadius: 4,
                transition: "background-color 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#004080")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#0a2e5c")
              }
            >
              {authorization ? "Actualizar" : "Guardar"}
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );
};

CreateAuthorizationForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  fetchAuthorizations: PropTypes.func.isRequired,
  authorization: PropTypes.object, // null o el objeto a editar
};

export default CreateAuthorizationForm;
